
/**
 * Vuex module to manage localized texts loaded from a JSON file.
 */

const state = () => ({
  texts: {}, // Stores the entire JSON content
  currentKey: STTEDRA_TYPE_PORTAL, // Key provided via environment variable
  loading: false, // Indicates if the texts are being loaded
  error: null, // Stores any error that occurs during loading
});

const mutations = {
  /**
   * Sets the texts in the state.
   * @param {Object} state - Vuex state.
   * @param {Object} texts - Texts JSON object.
   */
  SET_TEXTS(state, texts) {
    state.texts = texts;
  },

  /**
   * Sets the current key in the state.
   * @param {Object} state - Vuex state.
   * @param {string} key - The new key to set.
   */
  SET_CURRENT_KEY(state, key) {
    state.currentKey = key;
  },

  /**
   * Sets the loading state.
   * @param {Object} state - Vuex state.
   * @param {boolean} isLoading - Loading state.
   */
  SET_LOADING(state, isLoading) {
    state.loading = isLoading;
  },

  /**
   * Sets the error state.
   * @param {Object} state - Vuex state.
   * @param {string|null} error - Error message or null.
   */
  SET_ERROR(state, error) {
    state.error = error;
  },
};

const actions = {
  /**
   * Fetches the texts JSON from a specified URL.
   * @param {Function} commit - Vuex commit function.
   */
  async fetchTexts({rootGetters, commit}) {
    commit('SET_LOADING', true);
    commit('SET_ERROR', null);
    try {
      const year = rootGetters['app/getProductYear'];

      const response = await fetch(`/statics/locale/text-locale.json`, {
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const texts = await response.json();

      commit('SET_TEXTS', texts[year]);
    } catch (error) {
      commit('SET_ERROR', error.message);
      console.error('Error loading texts:', error);
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Updates the current key.
   * @param {Function} commit - Vuex commit function.
   * @param {string} key - The new key to set.
   */
  updateCurrentKey({commit}, key) {
    commit('SET_CURRENT_KEY', key);
  },
};

const getters = {
  /**
   * Retrieves the texts based on the current key.
   * @param {Object} state - Vuex state.
   * @returns {Object} - Texts corresponding to the current key.
   */
  getTexts(state) {
    if (!state.currentKey) return {};

    return state.texts[state.currentKey] || {};
  },

  /**
   * Retrieves a specific section of the texts based on a secondary key.
   * @param {Object} state - Vuex state.
   * @returns {Function} - Function to get a specific section.
   */
  getSectionTexts: (state) => (section) => {
    if (!state.currentKey) return {};

    return state.texts[state.currentKey]?.[section] || {};
  },

  /**
   * Retrieves the loading state.
   * @param {Object} state - Vuex state.
   * @returns {boolean} - Loading state.
   */
  isLoading(state) {
    return state.loading;
  },

  /**
   * Retrieves any error that occurred during loading.
   * @param {Object} state - Vuex state.
   * @returns {string|null} - Error message or null.
   */
  getError(state) {
    return state.error;
  },
};

const namespaced = true;

export default {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
